import { Dialog, Box, IconButton, Typography, useMediaQuery, type Theme } from '@mui/material';
import { ThemeProvider } from '@mui/system';
import { useEffect, useState } from 'react';

import { CallBackIcon } from './components/CallBackIcon';
import { CloseIcon } from './components/CloseIcon';
import { SlotsStep } from './components/SlotsStep';
import { STEPS } from './constants';
import { ContactDataStep } from './components/ContactDataStep';
import { ConfirmationStep } from './components/ConfirmationStep';
import { localCallBackBlockTheme, CallbackButton } from './styles';

import type { ConfirmationStepProps } from './components/ConfirmationStep';
import type { ContactDataStepProps } from './components/ContactDataStep';
import type { FC } from 'react';
import type { SlotsStepProps } from './components/SlotsStep';
import type { StepsValues } from './types';

export type CallBackBlockType = {
  ctaLabel: string;
  title: string;
} & SlotsStepProps &
  ContactDataStepProps &
  ConfirmationStepProps;

const STEPS_MAP: Record<StepsValues, any> = {
  [STEPS.SLOTS]: (props: SlotsStepProps) => <SlotsStep {...props} />,
  [STEPS.USER_DATA]: (props: ContactDataStepProps) => <ContactDataStep {...props} />,
  [STEPS.CONFIRMATION]: (props: ConfirmationStepProps) => <ConfirmationStep {...props} />,
};

const INITIAL_STEP = STEPS.SLOTS;

export const CallBackBlock: FC<CallBackBlockType> = ({ ctaLabel, title, ...props }) => {
  const [data, setData] = useState({});
  const [isOpen, setOpen] = useState(false);
  const [step, setStep] = useState<StepsValues>(INITIAL_STEP);

  const isDesktop = useMediaQuery<Theme>(({ breakpoints }) => breakpoints.up('md'));

  const closeDialog = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (isOpen && step !== INITIAL_STEP) {
      setStep(INITIAL_STEP);
    }
  }, [isOpen]);

  return (
    <ThemeProvider theme={localCallBackBlockTheme}>
      <div>
        {isDesktop ? (
          <CallbackButton
            variant={'outlined'}
            startIcon={<CallBackIcon width={'24px'} height={'24px'} />}
            size={'small'}
            onClick={() => setOpen(true)}
            sx={{ whiteSpace: 'nowrap', paddingX: '12px' }}
          >
            {ctaLabel}
          </CallbackButton>
        ) : (
          <IconButton color={'primary'} onClick={() => setOpen(true)} sx={{ minWidth: '44px' }}>
            <CallBackIcon />
          </IconButton>
        )}
        <Dialog
          open={isOpen}
          onClose={closeDialog}
          PaperProps={{
            sx: {
              width: '100%',
              height: '100%',
              maxWidth: { sm: '100%', md: '90vw', lg: '950px' },
              maxHeight: { sm: '100%', md: '80vh', lg: '820px' },
            },
          }}
        >
          <Box
            sx={{
              padding: '16px 16px 16px 32px',
              borderBottom: '1px solid #D3D4D6',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              [localCallBackBlockTheme.breakpoints.down('sm')]: {
                padding: '8px 8px 8px 16px',
              },
            }}
          >
            <Typography variant={'h5'}>{title}</Typography>
            <IconButton color={'primary'} onClick={closeDialog}>
              <CloseIcon />
            </IconButton>
          </Box>

          {STEPS_MAP[step]({ ...props, data, setData, setStep, closeDialog })}
        </Dialog>
      </div>
    </ThemeProvider>
  );
};
